// One monthly import: parsing progress while the documents are read, then
// the review table, then the commit. Admin only.
//
// Every extracted line is editable in place and nothing lands until the
// commit. Lines whose customer is not on file are flagged: committing
// creates those customers automatically, marked created-by-import so their
// profiles ask for the paperwork later.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  getActivityImport,
  parseNextImportFile,
  updateImportLine,
  commitActivityImport,
} from '../api.js'
import { useConfirm } from '../confirm.jsx'
import {
  Button,
  ButtonLink,
  EmptyState,
  Input,
  Kpi,
  KpiRow,
  Notice,
  Panel,
  Pill,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '../components/ui/index.js'
import { importStatusTone } from '../tones/activity.js'
import {
  importStatusLabel,
  activitySourceLabel,
  monthLabel,
  formatMoney,
  formatDate,
  SEPARATOR,
} from '../vocab.js'

// A small text cell that commits on blur or Enter and restores on Escape.
function TextCell({ value, onCommit, label, type = 'text', width = '7rem' }) {
  const [draft, setDraft] = useState(value ?? '')
  useEffect(() => setDraft(value ?? ''), [value])

  function commit() {
    const next = String(draft).trim()
    if (next === String(value ?? '')) return
    onCommit(next)
  }

  return (
    <Input
      type={type}
      value={draft}
      aria-label={label}
      style={{ width }}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.target.blur()
        if (event.key === 'Escape') setDraft(value ?? '')
      }}
    />
  )
}

export default function ActivityImportReview() {
  const { id } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [parsing, setParsing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const parseLoop = useRef(false)

  const load = useCallback(async () => {
    try {
      const result = await getActivityImport(id)
      setData(result)
      setLoadError(null)
      return result
    } catch (caught) {
      setLoadError(caught.message)
      return null
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  // Parsing runs one file per request until nothing is left, so a slow PDF
  // never trips a timeout and a dropped connection resumes where it stopped.
  const runParse = useCallback(async () => {
    if (parseLoop.current) return
    parseLoop.current = true
    setParsing(true)
    setActionError(null)
    try {
      let done = false
      while (!done) {
        const step = await parseNextImportFile(id)
        done = step.done
        await load()
      }
    } catch (caught) {
      setActionError(caught.message)
    } finally {
      parseLoop.current = false
      setParsing(false)
    }
  }, [id, load])

  useEffect(() => {
    if (!data) return
    const unparsed = data.import.files.some((file) => !file.parsedAt)
    if (
      data.import.status === 'DRAFT' &&
      unparsed &&
      data.parsingConfigured &&
      !parseLoop.current
    ) {
      runParse()
    }
  }, [data, runParse])

  async function patchLine(lineId, changes) {
    setActionError(null)
    try {
      const { line } = await updateImportLine(id, lineId, changes)
      setData((current) =>
        current
          ? {
              ...current,
              lines: current.lines.map((existing) => (existing.id === line.id ? line : existing)),
            }
          : current,
      )
    } catch (caught) {
      setActionError(caught.message)
    }
  }

  async function commit() {
    const included = data.lines.filter((line) => line.include)
    const creating = new Set(
      included
        .filter((line) => !line.customerId)
        .map((line) => line.customerName.trim().toLowerCase()),
    )
    const sure = await confirm(
      `Commit ${included.length} ${included.length === 1 ? 'line' : 'lines'} to ${monthLabel(
        data.import.serviceMonth,
      )}?${creating.size ? ` ${creating.size} new ${creating.size === 1 ? 'customer is' : 'customers are'} created automatically.` : ''}`,
      { title: 'Commit the import', confirmLabel: 'Commit' },
    )
    if (!sure) return
    setActionError(null)
    setCommitting(true)
    try {
      await commitActivityImport(id)
      navigate('/activity')
    } catch (caught) {
      setActionError(caught.message)
      setCommitting(false)
    }
  }

  if (loadError) {
    return (
      <Notice tone="bad">
        {loadError} <Link to="/activity/imports">Back to imports</Link>.
      </Notice>
    )
  }
  if (!data) return <div className="center-note">Loading</div>

  const batch = data.import
  const committed = batch.status === 'COMMITTED'
  const parsedCount = batch.files.filter((file) => file.parsedAt).length
  const stillParsing = parsedCount < batch.files.length
  const failedFiles = batch.files.filter((file) => file.parseError)

  const included = data.lines.filter((line) => line.include)
  const totalCents = included.reduce(
    (sum, line) => sum + (line.amount === null ? 0 : Math.round(Number(line.amount) * 100)),
    0,
  )
  const newCustomerNames = [
    ...new Map(
      included
        .filter((line) => !line.customerId)
        .map((line) => [line.customerName.trim().toLowerCase(), line.customerName.trim()]),
    ).values(),
  ]

  return (
    <>
      <header className="page-head">
        <h1>
          {activitySourceLabel(batch.source)}, {monthLabel(batch.serviceMonth)}
        </h1>
        <p className="support">
          <Pill tone={importStatusTone[batch.status]}>{importStatusLabel(batch.status)}</Pill>{' '}
          {[
            `${batch.files.length} ${batch.files.length === 1 ? 'file' : 'files'}`,
            batch.modelUsed ? `parsed by ${batch.modelUsed}` : null,
            batch.createdByName,
          ]
            .filter(Boolean)
            .join(SEPARATOR)}
        </p>
      </header>

      {actionError ? <Notice tone="bad">{actionError}</Notice> : null}

      <div className="toolbar">
        <ButtonLink to="/activity/imports">Back to imports</ButtonLink>
        <span className="spacer" />
        {!committed && !stillParsing ? (
          <Button variant="primary" onClick={commit} disabled={committing || included.length === 0}>
            {committing
              ? 'Committing'
              : `Commit ${included.length} ${included.length === 1 ? 'line' : 'lines'}`}
          </Button>
        ) : null}
      </div>

      {!data.parsingConfigured && stillParsing ? (
        <Notice tone="warn">
          Parsing is not configured. Set ANTHROPIC_ACTIVITY_API_KEY on the server, then reopen this
          import.
        </Notice>
      ) : null}

      {stillParsing && data.parsingConfigured ? (
        <Notice tone="neutral">
          {parsing
            ? `Reading the documents: ${parsedCount} of ${batch.files.length} files done. Lines appear below as each file finishes.`
            : `Parsing stopped at ${parsedCount} of ${batch.files.length} files.`}
          {!parsing ? (
            <>
              {' '}
              <Button variant="ghost" onClick={runParse}>
                Resume parsing
              </Button>
            </>
          ) : null}
        </Notice>
      ) : null}

      {failedFiles.length ? (
        <Notice tone="warn">
          {failedFiles.length === 1
            ? `${failedFiles[0].fileName} could not be read: ${failedFiles[0].parseError}`
            : `${failedFiles.length} files could not be read. Their names and errors are listed with the files below.`}{' '}
          The rest of the import is unaffected.
        </Notice>
      ) : null}

      {committed ? (
        <Notice tone="ok">
          Committed {formatDate(batch.committedAt?.slice(0, 10))}. The lines below are read only;
          delete the import from the imports page to take the month back.
        </Notice>
      ) : null}

      <KpiRow>
        <Kpi lead label="Included spend" value={formatMoney((totalCents / 100).toFixed(2))} />
        <Kpi label="Included lines" value={String(included.length)} />
        <Kpi label="Left out" value={String(data.lines.length - included.length)} />
        <Kpi label="New customers" value={String(newCustomerNames.length)} />
      </KpiRow>

      {newCustomerNames.length && !committed ? (
        <Notice tone="warn">
          Committing creates{' '}
          {newCustomerNames.length === 1 ? 'a customer that is' : 'customers that are'} not on file
          yet: {newCustomerNames.join(', ')}. Each is flagged on its profile until the paperwork
          lands.
        </Notice>
      ) : null}

      <Panel
        title="Extracted lines"
        support={
          data.lines.length
            ? 'Fix anything the documents got wrong, and untick lines that should not land.'
            : 'Nothing extracted yet.'
        }
        flush
      >
        {data.lines.length === 0 ? (
          <EmptyState title={stillParsing ? 'Reading the documents' : 'Nothing extracted'}>
            {stillParsing
              ? 'Lines appear here as each file finishes.'
              : 'The documents produced no activity lines. Check the files below for errors.'}
          </EmptyState>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th aria-label="Included" />
                <Th>Customer</Th>
                <Th>Work</Th>
                <Th numeric>Quantity</Th>
                <Th numeric>Amount</Th>
                <Th>Performed by</Th>
                <Th>Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.lines.map((line) => (
                <Tr key={line.id} className={line.include ? undefined : 'is-dimmed'}>
                  <Td>
                    <input
                      type="checkbox"
                      checked={line.include}
                      disabled={committed}
                      aria-label={`Include ${line.description} for ${line.customerName}`}
                      onChange={(event) => patchLine(line.id, { include: event.target.checked })}
                    />
                  </Td>
                  <Td>
                    {committed ? (
                      line.customerName
                    ) : (
                      <Select
                        value={line.customerId ?? ''}
                        onChange={(customerId) =>
                          patchLine(line.id, { customerId: customerId || null })
                        }
                        ariaLabel={`Customer for ${line.description}`}
                        style={{ width: '15rem' }}
                        options={[
                          { value: '', label: `New: ${line.customerName}` },
                          ...data.customers.map((customer) => ({
                            value: customer.id,
                            label: customer.name,
                          })),
                        ]}
                      />
                    )}
                    {!line.customerId && !committed ? (
                      <div className="ui-cell-meta">
                        <Pill tone="warn">New customer</Pill>
                      </div>
                    ) : null}
                  </Td>
                  <Td className="wraps">
                    {committed ? (
                      line.description
                    ) : (
                      <TextCell
                        value={line.description}
                        label={`Work for ${line.customerName}`}
                        width="16rem"
                        onCommit={(description) =>
                          description ? patchLine(line.id, { description }) : undefined
                        }
                      />
                    )}
                    <div className="ui-cell-meta">
                      {[line.sourceFileName, line.parserNote].filter(Boolean).join(SEPARATOR)}
                    </div>
                  </Td>
                  <Td numeric>
                    {committed ? (
                      (line.quantity ?? '')
                    ) : (
                      <TextCell
                        type="number"
                        value={line.quantity ?? ''}
                        label={`Quantity for ${line.customerName}`}
                        width="5.5rem"
                        onCommit={(quantity) =>
                          patchLine(line.id, {
                            quantity: quantity === '' ? null : Number(quantity),
                          })
                        }
                      />
                    )}
                  </Td>
                  <Td numeric>
                    {committed ? (
                      line.amount === null ? (
                        ''
                      ) : (
                        formatMoney(line.amount)
                      )
                    ) : (
                      <TextCell
                        type="number"
                        value={line.amount ?? ''}
                        label={`Amount for ${line.customerName}`}
                        width="7rem"
                        onCommit={(amount) =>
                          patchLine(line.id, { amount: amount === '' ? null : Number(amount) })
                        }
                      />
                    )}
                  </Td>
                  <Td>
                    {committed ? (
                      (line.franchiseeName ?? '')
                    ) : (
                      <Select
                        value={line.franchiseeId ?? ''}
                        onChange={(franchiseeId) =>
                          patchLine(line.id, { franchiseeId: franchiseeId || null })
                        }
                        ariaLabel={`Franchisee for ${line.description}`}
                        style={{ width: '13rem' }}
                        options={[
                          {
                            value: '',
                            label: line.franchiseeName
                              ? `Unmatched: ${line.franchiseeName}`
                              : 'Not attributed',
                          },
                          ...data.franchisees.map((franchisee) => ({
                            value: franchisee.id,
                            label: franchisee.name,
                          })),
                        ]}
                      />
                    )}
                    {!line.franchiseeId && line.franchiseeName && !committed ? (
                      <div className="ui-cell-meta">
                        <Pill tone="warn">Not on the roster</Pill>
                      </div>
                    ) : null}
                  </Td>
                  <Td>
                    {line.serviceDate ? (
                      formatDate(line.serviceDate)
                    ) : (
                      <span className="muted">Not stated</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Panel>

      <Panel title="Files" support="What was uploaded and how each file parsed.">
        <div className="rows">
          {batch.files.map((file) => (
            <div key={file.id} className="row">
              <div className="row-main">
                <div className="row-primary wraps">{file.fileName}</div>
                <div className="row-meta wraps">
                  {file.parseError
                    ? file.parseError
                    : file.parsedAt
                      ? `${file.lineCount} ${file.lineCount === 1 ? 'line' : 'lines'} extracted`
                      : 'Waiting to parse'}
                </div>
              </div>
              <div className="row-trail">
                {file.parseError ? (
                  <Pill tone="bad">Failed</Pill>
                ) : file.parsedAt ? (
                  <Pill tone="ok">Parsed</Pill>
                ) : (
                  <Pill tone="neutral">Queued</Pill>
                )}
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </>
  )
}
